import React, { useMemo, useState } from "react";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import { formVacio, validarForm, crearServicio } from "../../utils/servicios";
import ServicioForm from "./ServicioForm";
import { XMarkIcon } from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

// Alta de un servicio (FRA-348): un solo paso con el formulario compartido.
// Nada obligatorio más que el nombre y el precio; quién lo pone y sus piezas
// se contestan si aplican. Una renta de salón se da de alta con dos campos;
// un calentador con sus seis piezas, de una sentada.
const ServicioCreateDialog = ({ open, onClose, onCreated, inicial = null }) => {
  const { selectedVenueId, proveedores, conceptos } = useVenue();
  const { userInfo, user } = useAuth();
  const [form, setForm] = useState(() => ({ ...formVacio(), ...(inicial || {}) }));
  const [saving, setSaving] = useState(false);

  const proveedoresPorId = useMemo(() => {
    const map = {};
    (proveedores || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [proveedores]);

  const conceptosPorId = useMemo(() => {
    const map = {};
    (conceptos || []).forEach((c) => { if (c?.id) map[c.id] = c; });
    return map;
  }, [conceptos]);

  const cerrar = () => {
    setForm({ ...formVacio(), ...(inicial || {}) });
    onClose?.();
  };

  const handleCrear = async () => {
    const error = validarForm(form, { conceptosPorId });
    if (error) {
      showAlert(error, "error");
      return;
    }
    setSaving(true);
    try {
      const id = await crearServicio({
        venueId: selectedVenueId,
        form,
        user,
        userInfo,
        proveedoresPorId,
        conceptosPorId,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.SERVICIO_CREATE,
          entity: "servicio",
          entityId: id,
          entityName: form.nombre.trim(),
          details: {
            tipo: form.tipo,
            proveedorId: form.deProveedor ? form.proveedorId : null,
            itemizado: !!form.itemizado,
            incluye: form.tipo === "renta" ? (form.incluye || []).length : 0,
            unidad: form.unidad,
          },
        },
        userInfo,
        user
      );
      showAlert("Servicio creado", "success");
      cerrar();
      onCreated?.(id);
    } catch (err) {
      showAlert(`Error al crear. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={cerrar} />
      <div className="relative w-full max-w-2xl rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[92vh]">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
          <h2 className="text-base font-semibold text-zinc-900 dark:text-white">
            Nuevo servicio
          </h2>
          <button type="button" onClick={cerrar} className="ml-auto text-zinc-400 hover:text-zinc-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          <ServicioForm form={form} setForm={setForm} proveedores={proveedores} modo="alta" autoFocus />
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
          <button type="button" onClick={cerrar} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleCrear}
            disabled={saving}
            className="px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}
          >
            {saving ? "Creando…" : "Crear servicio"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServicioCreateDialog;
